import * as React from 'react';

import { Button } from '@cotera/client/v0/components/ui/button';
import { cn } from '@cotera/client/v0/lib/utils';
import { Icon } from '../../../../ui/icon/icon';
import type {
  CommentAuthor,
  CommentReply,
} from '../../../../ui/tiptap/extensions/comment-mark';
import { CommentComposer } from './comment-composer';
import { CommentMessage, CommentMessageMenu } from './comment-message';
import type { CommentThreadSummary } from './comment-threads';

/** How tall a collapsed card is allowed to get before it gives way to "… more". */
const COLLAPSED_MAX_HEIGHT_PX = 150;

/**
 * Dissolves the last stretch of a collapsed card that has more to say.
 *
 * Snapping the height to whole lines cannot work here: a card is a stack of bylines,
 * an indented quote and paragraphs of wrapped text, so no single height lands on a
 * line boundary for every thread — most of them get sliced through the middle of a
 * line, which is what a crop looks like. Fading the bottom two lines to nothing means
 * the text runs out instead of stopping, whatever happens to be there.
 */
const COLLAPSED_FADE =
  'linear-gradient(to bottom, #000 calc(100% - 2.5rem), transparent 100%)';

export interface CommentThreadPanelProps {
  thread: CommentThreadSummary;
  author: CommentAuthor | null;
  canEdit: boolean;
  onReply: (body: string) => void;
  onToggleResolved?: () => void;
  /** Rewrites one message. Omitted, Edit is offered on none of them. */
  onEditReply?: (replyId: string, body: string) => void;
  /** Drops one message. Dropping the first takes the thread with it. */
  onDeleteReply?: (replyId: string) => void;
  /**
   * Caps the height and hides the composer, for a card sitting in the margin of a
   * document nobody has clicked into yet. Expanding is the reader's move.
   */
  collapsed?: boolean;
  onExpand?: () => void;
  className?: string;
}

/**
 * A comment thread as it appears against the document: the conversation, and a box to
 * add to it.
 *
 * The same card in both placements the document has for one — stacked in the margin
 * beside the line, or floating under it where there is no margin to stack in. What
 * changes between them is where it is put and how much of it is shown, which is the
 * caller's business; a thread reads the same either way.
 *
 * It is also the only place a thread is read and answered. The comments index beside
 * it hands conversations here rather than holding one of its own.
 */
export function CommentThreadPanel({
  thread,
  author,
  canEdit,
  onReply,
  onToggleResolved,
  onEditReply,
  onDeleteReply,
  collapsed = false,
  onExpand,
  className,
}: CommentThreadPanelProps) {
  const [replyBody, setReplyBody] = React.useState('');
  const [editingReplyId, setEditingReplyId] = React.useState<string | null>(
    null
  );
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = React.useState(false);

  // Only the person who wrote it, and only where the card is open enough to hold an
  // editor. Rewriting someone else's words under their own name is not an edit.
  const mayEdit = (reply: CommentReply): boolean =>
    !collapsed &&
    canEdit &&
    onEditReply !== undefined &&
    author !== null &&
    reply.authorId === author.id;

  const showsResolve = !collapsed && canEdit && onToggleResolved !== undefined;

  // The opening message's menu is rendered up in the card's corner beside resolve
  // rather than on the message, so the two sit in one row.
  const openingMessage = thread.replies[0];
  const openingMenu =
    !collapsed && openingMessage !== undefined ? (
      <CommentMessageMenu
        reply={openingMessage}
        {...(mayEdit(openingMessage) && {
          onStartEdit: () => setEditingReplyId(openingMessage.id),
        })}
        {...(canEdit &&
          onDeleteReply !== undefined && {
            onDelete: () => onDeleteReply(openingMessage.id),
          })}
      />
    ) : null;

  // Collapsed cards open on a click; expanded ones have their own controls inside.
  const expandable = collapsed && onExpand !== undefined;
  const Container = expandable ? 'button' : 'div';

  // Collapsing the card, or moving to another thread, abandons an edit in progress
  // rather than leaving a hidden box open behind it.
  React.useEffect(() => {
    setEditingReplyId(null);
  }, [collapsed, thread.commentId]);

  React.useLayoutEffect(() => {
    const node = bodyRef.current;
    if (node === null || !collapsed) {
      setOverflowing(false);
      return;
    }
    setOverflowing(node.scrollHeight > COLLAPSED_MAX_HEIGHT_PX + 1);
  }, [collapsed, thread.replies]);

  const submitReply = () => {
    const trimmed = replyBody.trim();
    if (trimmed === '') {
      return;
    }
    onReply(trimmed);
    setReplyBody('');
  };

  return (
    <div
      className={cn(
        'group relative flex max-h-full flex-col overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground',
        // One soft layer in the app's own colour space, close to what the artifact
        // card carries — in the focus view these two sit side by side, and a card
        // lifted further off the page than the document it annotates reads as the
        // more important of the two.
        'shadow-[0_4px_12px_oklch(0.45_0.02_91.45_/_0.06)]',
        className
      )}
    >
      {/* One row: resolve, then the menu for the message that opens the thread.
          Both of these used to be absolutely positioned inside different boxes — the
          menu within the message, resolve on the card — so which sat left of which
          came down to padding. Here it is simply the order they are written in, and
          swapping them is swapping these two lines.

          On approach, rather than taking a header row of their own: the card is
          mostly read, and a permanent bar of controls over the first message would be
          the loudest thing in it. */}
      {showsResolve || openingMenu !== null ? (
        <div className="absolute right-1.5 top-1.5 z-10 flex items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          {showsResolve ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={
                thread.resolved ? 'Reopen comment' : 'Resolve comment'
              }
              title={thread.resolved ? 'Reopen' : 'Resolve'}
              className={cn(
                'size-7 bg-popover p-0',
                thread.resolved && 'text-green-600'
              )}
              onClick={onToggleResolved}
            >
              <Icon icon="check" className="h-4 w-4" />
            </Button>
          ) : null}
          {openingMenu}
        </div>
      ) : null}
      {/* Collapsed, the whole card is the way in — not just the "... more" at the
          foot of it, which a short comment does not even have. Clicking anywhere on
          it opens the thread, which is what someone reaching for a card wants: to
          read the rest of it, or to answer.

          A button rather than a div with a handler, so it answers the keyboard too.
          Nothing interactive is rendered inside a collapsed card — the corner actions
          and the composer both wait for it to open — so there is no button nested in
          this one.

          The padding lives out here, on the unclipped box. Inside the clipped one it
          was part of what overflow threw away: the text ran flush to the card's edge
          and the last line had nothing under it. */}
      <Container
        {...(expandable && {
          type: 'button' as const,
          onClick: onExpand,
          'aria-expanded': false,
          'aria-label': 'Open comment thread',
        })}
        className={cn(
          // `min-h-0` so this pane, not the composer under it, is what gives way
          // when the card is capped to the room the viewport has.
          'relative block w-full min-h-0 p-3 text-left',
          expandable && 'cursor-pointer'
        )}
      >
        <div
          ref={bodyRef}
          style={
            collapsed
              ? {
                  maxHeight: COLLAPSED_MAX_HEIGHT_PX,
                  // The fade belongs to text that carries on past the cut, and short
                  // of that it is just a message going grey at the bottom for no
                  // reason. Same condition as the "... more" it leads into.
                  ...(overflowing && {
                    maskImage: COLLAPSED_FADE,
                    WebkitMaskImage: COLLAPSED_FADE,
                  }),
                }
              : undefined
          }
          className={cn(
            'flex flex-col gap-2.5',
            // Expanded, the card grows with the thread and only scrolls once it runs
            // out of room — either most of the window, or whatever the floating
            // placement could give it, whichever is smaller. A tighter cap made
            // ordinary threads scroll, and a scrolled box cuts its text off mid-line.
            collapsed ? 'overflow-hidden' : 'max-h-[60vh] overflow-y-auto'
          )}
        >
          {thread.replies.map((reply, index) => (
            <CommentMessage
              key={reply.id}
              reply={reply}
              {...(index === 0 && { quote: thread.anchorText })}
              {...(editingReplyId === reply.id && {
                editing: true,
                onSaveEdit: (body: string) => {
                  onEditReply?.(reply.id, body);
                  setEditingReplyId(null);
                },
                onCancelEdit: () => setEditingReplyId(null),
              })}
              {...(mayEdit(reply) && {
                onStartEdit: () => setEditingReplyId(reply.id),
              })}
              {...(!collapsed &&
                canEdit &&
                onDeleteReply !== undefined && {
                  onDelete: () => onDeleteReply(reply.id),
                })}
              {...(index === 0 && { hostsOwnMenu: true })}
            />
          ))}
        </div>

        {collapsed && overflowing ? (
          <span
            // Sits at the foot of the fade, where the text has already given out, so
            // it reads as the end of the card rather than a label dropped onto a
            // line of someone's sentence.
            //
            // Opaque under the words themselves and clearing to nothing to their
            // left: the mask above thins the text but does not remove it, and a
            // half-faded letter showing through the one piece of the card you are
            // meant to click is the thing that looks unfinished.
            className="absolute bottom-3 right-3 bg-gradient-to-l from-popover from-65% to-transparent pl-12 text-sm leading-5 text-muted-foreground"
          >
            ... more
          </span>
        ) : null}
      </Container>

      {!collapsed && canEdit ? (
        <CommentComposer
          value={replyBody}
          onChange={setReplyBody}
          onSubmit={submitReply}
          placeholder="Add a comment..."
          submitLabel="Send reply"
          author={author}
          chrome="plain"
        />
      ) : null}
    </div>
  );
}
